import {Injectable} from "@angular/core";
import {ButtonFilterConfigDTO, FilterDashboardDTO} from "../dto/dashboard.dto";
import {
  ButtonFilterConfig,
  ChartSeries,
  ChartXAxis,
  FilterDate,
  FilterDateKey,
  ReportCategories,
  VenueOverall
} from "../constant/dashboard.constant";
import moment from 'moment';
import {ApexAxisChartSeries, ApexChart, ApexXAxis, ApexYAxis} from "ng-apexcharts";
import {BehaviorSubject, catchError, filter, map, Observable, of, switchMap, tap} from "rxjs";
import {DashboardRepository} from "../repository/dashboard.repository";
import {ToastrService} from "ngx-toastr";
import {
  DashboardMenuList,
  DataRatedMenus,
  DataReportCategory,
  DataReviewedMenus,
  DataVenueOverall
} from "../repository/entity/dashboard.entity";

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private _btnFilterConfig: ButtonFilterConfigDTO[] = ButtonFilterConfig;
  private _chartSeries$ = new BehaviorSubject<ApexAxisChartSeries>(ChartSeries);
  private _chartXAxis$ = new BehaviorSubject<ApexXAxis>(ChartXAxis);
  private _filterDate$ = new BehaviorSubject<FilterDashboardDTO>(FilterDate);
  private _ratedMenus$ = new BehaviorSubject<DataRatedMenus[]>([]);
  private _reviewedMenus$ = new BehaviorSubject<DataReviewedMenus[]>([]);
  private _venueOverall$ = new BehaviorSubject<DataVenueOverall>(VenueOverall);
  private _venueSection$ = new BehaviorSubject<DataVenueOverall[]>([VenueOverall]);
  private _reportCategories$ = new BehaviorSubject<DataReportCategory[]>(ReportCategories);
  private _listMenu$ = new BehaviorSubject<DashboardMenuList[]>([]);

  constructor(private dashboardRepo: DashboardRepository, private toastService: ToastrService) {
  }

  private _chart: ApexChart;

  public get chart(): ApexChart {
    return this._chart;
  }

  public set chart(data: ApexChart) {
    this._chart = data
  }

  public get venueOverall(): Observable<DataVenueOverall> {
    return this._venueOverall$;
  }

  public set venueOverall(data: DataVenueOverall) {
    this._venueOverall$.next(data)
  }

  public get venueSection(): Observable<DataVenueOverall[]> {
    return this._venueSection$;
  }

  public set venueSection(data: DataVenueOverall[]) {
    this._venueSection$.next(data)
  }

  private _chartYAxis: ApexYAxis;

  public get chartYAxis(): ApexYAxis {
    return this._chartYAxis;
  }

  public set chartYAxis(data: ApexYAxis) {
    this._chartYAxis = data;
  }

  public get ratedMenus(): Observable<DataRatedMenus[]> {
    return this._ratedMenus$;
  }

  public set ratedMenus(data: DataRatedMenus[]) {
    this._ratedMenus$.next(data);
  }

  public get reviewedMenus(): Observable<DataReviewedMenus[]> {
    return this._reviewedMenus$;
  }

  public set reviewedMenus(data: DataReviewedMenus[]) {
    this._reviewedMenus$.next(data);
  }

  public get buttonConfig(): ButtonFilterConfigDTO[] {
    return this._btnFilterConfig;
  }

  public get chartXAxis(): Observable<ApexXAxis> {
    return this._chartXAxis$.asObservable();
  }

  public set chartXAxis(data: ApexXAxis) {
    this._chartXAxis$.next(data)
  }

  public get chartSeries(): Observable<ApexAxisChartSeries> {
    return this._chartSeries$.asObservable();
  }

  public set chartSeries(data: ApexAxisChartSeries) {
    this._chartSeries$.next(data)
  }

  public get filterDate(): Observable<FilterDashboardDTO> {
    return this._filterDate$.asObservable();
  }

  public set filterDate(date: FilterDashboardDTO) {
    this._filterDate$.next(date);
  }

  public get reportCategory(): Observable<DataReportCategory[]> {
    return this._reportCategories$;
  }

  public set reportCategory(data: DataReportCategory[]) {
    this._reportCategories$.next(data)
  }

  public get listMenu(): Observable<DashboardMenuList[]> {
    return this._listMenu$;
  }

  public set listMenu(value: DashboardMenuList[]) {
    this._listMenu$.next(value);
  }

  initChart() {
    this.chart = {
      type: 'area',
      toolbar: {
        show: false
      },
      height: 322,
      fontFamily: "Inter",
    };

    this.chartYAxis = {max: 5}
  }

  consumeDataChart(date: FilterDashboardDTO) {
    const chart$ = date.reportCategory === "venue" ? this.dashboardRepo.fetchDataChart(date) : this.dashboardRepo.fetchMenuDataChart(date);
    return chart$.pipe(
      catchError(e => {
        this.toastService.error(e.error.message);
        return of({
          result: {
            labels: [e.error.message],
            series: [{name: e.error.message, data: [0]}]
          }
        })
      }),
      map(d => d.result)
    );
  }

  fetchDataChart() {
    return this.filterDate.pipe(
      filter(filterDate => filterDate.branch !== ""),
      switchMap(date => this.consumeDataChart(date)),
      tap(d => {
        this.chartSeries = d.series;
        this.chartXAxis = {floating: false, categories: d.labels};
      })
    )
  }

  fetchDataOverall() {
    return this.filterDate.pipe(
      filter(filterDate => filterDate.branch !== ""),
      switchMap(filterDate => {
        const category = filterDate.reportCategory;
        return category === "venue" ? this.dashboardRepo.fetchVenueOverall(filterDate) : this.dashboardRepo.fetchMenuOverall(filterDate);
      }),
      map(d => d.result),
      tap(d => this.venueOverall = d)
    )
  }

  fetchDataVenueSection() {
    return this.filterDate.pipe(
      filter(filterDate => filterDate.branch !== ""),
      switchMap(date => this.dashboardRepo.fetchVenueSection(date)),
      map(d => d.result),
      tap(d => this.venueSection = d)
    )
  }

  fetchDataRatedMenus() {
    return this.filterDate.pipe(
      filter(filterDate => filterDate.branch !== ""),
      switchMap(date => this.dashboardRepo.fetchRatedMenus(date)),
      map(d => d.result),
      tap(d => this.ratedMenus = d)
    )
  }

  fetchDataReviewedMenus() {
    return this.filterDate.pipe(
      filter(filterDate => filterDate.branch !== ""),
      switchMap(date => this.dashboardRepo.fetchReviewedMenus(date)),
      map(d => d.result),
      tap(d => this.reviewedMenus = d)
    )
  }

  fetchListMenu() {
    return this.dashboardRepo.getMenu().pipe(
      map(d => d.result.data),
      tap(d => this.listMenu = d),
    );
  }

  convertDate(date: Date) {
    return moment(date).format("YYYY-MM-DD");
  }

  getQuickFilterDate(key: string) {
    const date = moment();
    const currentDate = date.format("YYYY-MM-DD");
    let prevDate = currentDate;

    const findKey = this._btnFilterConfig.map(v => v.filter).find(v => v === key);
    if (findKey) {
      switch (findKey) {
        case FilterDateKey.month:
          prevDate = date.add(-1, "month").format("YYYY-MM-DD");
          break;
        case FilterDateKey.week:
          prevDate = date.add(-1, "week").format('YYYY-MM-DD');
          break;
        case FilterDateKey.year:
          prevDate = date.add(-1, "year").format('YYYY-MM-DD');
          break;
        default:
          prevDate = currentDate;
          break;
      }
    }
    return {currentDate, prevDate}
  }

  convertQuickFilterDate(key: string, branch: string) {
    const {currentDate, prevDate} = this.getQuickFilterDate(key);
    const currentFilterDate = this._filterDate$.value;
    this.filterDate = {...currentFilterDate, currentDate, prevDate, branch};
  }

  setFilterCategory(reportCategory: string, branch: string, menuIds: string) {
    const currentFilterDate = this._filterDate$.value;
    this.filterDate = {...currentFilterDate, reportCategory, branch, menuIds}
  }

  updateDateFilterDate(currentDate: string, prevDate: string) {
    const currentFilterDate = this._filterDate$.value;
    this.filterDate = {...currentFilterDate, currentDate, prevDate};
  }

}
